import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as customerService from '../../services/customerService';

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params, { rejectWithValue }) => {
    try {
      const res = await customerService.getCustomers(params);
      const body = res.data;
      return {
        customers: body.data || body.customers || [],
        totalPages: body.pagination?.totalPages || body.totalPages || 1,
        currentPage: body.pagination?.page || body.page || 1,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch customers');
    }
  }
);

export const fetchCustomer = createAsyncThunk(
  'customers/fetchCustomer',
  async (id, { rejectWithValue }) => {
    try {
      const res = await customerService.getCustomer(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch customer');
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      const res = await customerService.createCustomer(customerData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create customer');
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, customerData }, { rejectWithValue }) => {
    try {
      const res = await customerService.updateCustomer(id, customerData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update customer');
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await customerService.deleteCustomer(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete customer');
    }
  }
);

export const getTransactions = createAsyncThunk(
  'customers/getTransactions',
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await customerService.getTransactions(id, params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const recordPayment = createAsyncThunk(
  'customers/recordPayment',
  async ({ id, paymentData }, { rejectWithValue }) => {
    try {
      const res = await customerService.recordPayment(id, paymentData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to record payment');
    }
  }
);

export const addCredit = createAsyncThunk(
  'customers/addCredit',
  async ({ id, creditData }, { rejectWithValue }) => {
    try {
      const res = await customerService.addCredit(id, creditData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add credit');
    }
  }
);

export const writeOff = createAsyncThunk(
  'customers/writeOff',
  async ({ id, amount, reason }, { rejectWithValue }) => {
    try {
      const res = await customerService.writeOff(id, { amount, reason });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to write off');
    }
  }
);

export const blockCustomer = createAsyncThunk(
  'customers/blockCustomer',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const res = await customerService.blockCustomer(id, { reason });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to block customer');
    }
  }
);

export const getReminders = createAsyncThunk(
  'customers/getReminders',
  async (_, { rejectWithValue }) => {
    try {
      const res = await customerService.getReminders();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch reminders');
    }
  }
);

export const createReminder = createAsyncThunk(
  'customers/createReminder',
  async (reminderData, { rejectWithValue }) => {
    try {
      const res = await customerService.createReminder(reminderData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create reminder');
    }
  }
);

export const getCreditSummary = createAsyncThunk(
  'customers/getCreditSummary',
  async (_, { rejectWithValue }) => {
    try {
      const res = await customerService.getCreditSummary();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch credit summary');
    }
  }
);

export const exportCustomers = createAsyncThunk(
  'customers/exportCustomers',
  async (params, { rejectWithValue }) => {
    try {
      const res = await customerService.exportCustomers(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Export failed');
    }
  }
);

const initialState = {
  customers: [],
  customer: null,
  loading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,
  transactions: [],
  reminders: [],
  creditSummary: null,
};

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomer: (state) => {
      state.customer = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload.customers || action.payload.data || action.payload;
        state.totalPages = action.payload.totalPages || action.payload.pages || 1;
        state.currentPage = action.payload.page || 1;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customer = action.payload.customer || action.payload;
      })
      .addCase(fetchCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers.unshift(action.payload.customer || action.payload);
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.customer || action.payload;
        const idx = state.customers.findIndex((c) => c._id === updated._id);
        if (idx !== -1) state.customers[idx] = updated;
        if (state.customer?._id === updated._id) state.customer = updated;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.customers = state.customers.filter((c) => c._id !== action.payload);
        if (state.customer?._id === action.payload) state.customer = null;
      })
      .addCase(getTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions || action.payload.data || action.payload;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(recordPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordPayment.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.customer || action.payload;
        if (updated._id) {
          const idx = state.customers.findIndex((c) => c._id === updated._id);
          if (idx !== -1) state.customers[idx] = updated;
          if (state.customer?._id === updated._id) state.customer = updated;
        }
      })
      .addCase(recordPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addCredit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCredit.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.customer || action.payload;
        if (updated._id) {
          const idx = state.customers.findIndex((c) => c._id === updated._id);
          if (idx !== -1) state.customers[idx] = updated;
          if (state.customer?._id === updated._id) state.customer = updated;
        }
      })
      .addCase(addCredit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(writeOff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(writeOff.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.customer || action.payload;
        if (updated._id) {
          const idx = state.customers.findIndex((c) => c._id === updated._id);
          if (idx !== -1) state.customers[idx] = updated;
          if (state.customer?._id === updated._id) state.customer = updated;
        }
      })
      .addCase(writeOff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(blockCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(blockCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.customer || action.payload;
        if (updated._id) {
          const idx = state.customers.findIndex((c) => c._id === updated._id);
          if (idx !== -1) state.customers[idx] = updated;
          if (state.customer?._id === updated._id) state.customer = updated;
        }
      })
      .addCase(blockCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getReminders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReminders.fulfilled, (state, action) => {
        state.loading = false;
        state.reminders = action.payload.reminders || action.payload.data || action.payload;
      })
      .addCase(getReminders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReminder.fulfilled, (state, action) => {
        state.loading = false;
        state.reminders.unshift(action.payload.reminder || action.payload);
      })
      .addCase(createReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getCreditSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCreditSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.creditSummary = action.payload.summary || action.payload;
      })
      .addCase(getCreditSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(exportCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportCustomers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCustomer, clearError, setPage } = customerSlice.actions;
export default customerSlice.reducer;
